import React, { useEffect, useRef, useState } from "react";
import "./AboutUs.css";
import aboutMainImg from "../assets/about/abou.jpeg";
import aboutSecondaryImg from "../assets/about/abou.jpeg";

const stats = [
  { value: "65+", label: "Projects Completed" },
  { value: "$6M+", label: "Contract Value" },
  { value: "22+", label: "Years Experience" },
  { value: "100%", label: "Completion Rate" },
];

const highlights = [
  {
    icon: "◎",
    title: "Registered in 2021, Operations since July 2021",
    desc: "Growing rapidly with 65+ completed projects across Singapore infrastructure.",
  },
  {
    icon: "✓",
    title: "ISO 9001:2015 & ISO 45001:2018 Certified",
    desc: "BizSafe Star rated — Safety First is embedded in every project we execute.",
  },
  {
    icon: "⚇",
    title: "Experienced Leadership Team",
    desc: "Directors with 22–23 years in Singapore construction & geotechnical fields.",
  },
  {
    icon: "◈",
    title: "100% Project Completion Rate",
    desc: "Every project delivered on time for LTA, PUB and private clients.",
  },
];

// NOTE: `image` fields removed for now — photos are hidden until real
// team photos are ready. `initials` is used to render a badge instead.
const teamMembers = [
  {
    initials: "KN",
    name: "K. Nedunchezian",
    tag: "DIRECTOR & OPERATIONS HEAD",
    desc: "25+ years in Construction & Geotechnical field",
  },
  {
    initials: "MS",
    name: "M. Subenthiran",
    tag: "GENERAL MANAGER",
    desc: "25+ years in Construction & Ground Improvement",
  },
  {
    initials: "KG",
    name: "Killiyappan Gopal",
    tag: "MAJOR SHAREHOLDER & DIRECTOR",
    desc: "30+ years experience director of JAK Engineering & Jae Hwan Construction.",
  },
];

const certifications = [
  "SBI (I&M)/SI",
  "SB (GS)",
  "CR15",
  "CR12",
  "ISO 45001:2018",
  "ISO 9001:2015",
  "BIZ SAFE STAR",
  "SAC Accreditation",
];

const values = [
  {
    icon: "🛡",
    title: "Safety First",
    desc: "Every project begins and ends with safety as the non-negotiable foundation.",
  },
  
  {
    icon: "📋",
    title: "Sequential Work",
    desc: "Structured, methodical execution ensures reliability and consistency.",
  },
  {
    icon: "⚙",
    title: "Quality & Production",
    desc: "Precision engineering balanced with efficient project delivery on all sites.",
  },
];

function useIntersect(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      options
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

function AnimatedStat({ value, label, delay }) {
  const [ref, visible] = useIntersect({ threshold: 0.3 });
  return (
    <div
      ref={ref}
      className={"about-stat-item" + (visible ? " visible" : "")}
      style={{ transitionDelay: delay + "ms" }}
    >
      <div className="about-stat-value">{value}</div>
      <div className="about-stat-label">{label}</div>
    </div>
  );
}

export default function About() {
  const [storyRef, storyVisible] = useIntersect({ threshold: 0.1 });
  const [valuesRef, valuesVisible] = useIntersect({ threshold: 0.1 });
  const [teamRef, teamVisible] = useIntersect({ threshold: 0.1 });
  const [certRef, certVisible] = useIntersect({ threshold: 0.1 });

  return (
    <div className="about-page" >

      {/* TOP BANNER */}
      <section className="about-top-banner">
        <div className="about-top-banner__grid" />
        <div className="about-top-banner__glow" />
        <h1 className="about-top-banner__title">
          About <span className="about-accent">Us</span>
        </h1>
      </section>

      

      {/* STORY SECTION */}
      <section className="about-story" ref={storyRef}>
        <div className="about-story-inner">

          <div className={"about-story-images" + (storyVisible ? " visible" : "")}>
            <div className="about-img-main">
              <div className="about-img-placeholder main-img">
                <img src={aboutMainImg} alt="Geotechnical Works" className="about-img-actual" />
              </div>
              <div className="about-years-badge">
                <span className="about-years-num">4+</span>
                <span className="about-years-text">YEARS OF<br />OPERATION</span>
              </div>
            </div>
            <div className="about-img-secondary">
              <div className="about-img-placeholder sec-img">
                <img src={aboutSecondaryImg} alt="Infrastructure" className="about-img-actual" />
              </div>
            </div>
          </div>

          <div className={"about-story-text" + (storyVisible ? " visible" : "")}>
            <div className="about-section-label">
              <span className="about-label-line" />
              ABOUT US
            </div>
            <h2 className="about-story-title">
              YOUR TRUSTED PARTNER<br />
              IN <span className="about-accent">GEOTECHNICAL</span><br />
              ENGINEERING
            </h2>
            <p className="about-story-desc">
              GSK Geotech Pte Ltd was registered in 2021 and commenced operations in July 2021.
              Based in Singapore, we specialise in Geotechnical Investigation, Ground Improvement,
              Grouting Works, Recharge &amp; Relief Wells, Instrumentation &amp; Monitoring, &amp; Pipe Jacking — serving LTA, PUB, HDB, under leading private contractors
              across the island.
            </p>

            <div className="about-highlights">
              {highlights.map((h, i) => (
                <div key={i} className="about-highlight-card">
                  <div className="about-highlight-icon">{h.icon}</div>
                  <div className="about-highlight-body">
                    <div className="about-highlight-title">{h.title}</div>
                    <div className="about-highlight-desc">{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <button className="about-cta-btn">
              WORK WITH US <span className="about-btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>
      {/* STATS BAR */}
      <section className="about-stats-bar">
        {stats.map((s, i) => (
          <AnimatedStat key={i} value={s.value} label={s.label} delay={i * 100} />
        ))}
      </section>

      {/* VALUES */}
      <section className="about-values" ref={valuesRef}>
        <div className="about-values-inner">
          <div className="about-section-label centered">
            <span className="about-label-line" />
            OUR CORE VALUES
            <span className="about-label-line" />
          </div>
          <h2 className={"about-values-title" + (valuesVisible ? " visible" : "")}>
            What Drives <span className="about-accent">Every Project</span>
          </h2>
          <div className="about-values-grid">
            {values.map((v, i) => (
              <div
                key={i}
                className={"about-value-card" + (valuesVisible ? " visible" : "")}
                style={{ transitionDelay: i * 150 + "ms" }}
              >
                <div className="about-value-icon">{v.icon}</div>
                <h3 className="about-value-title">{v.title}</h3>
                <p className="about-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="about-certifications" ref={certRef}>
        <div className="about-cert-inner">
          <h2 className={"about-cert-title" + (certVisible ? " visible" : "")}>
            Certified &amp; <em className="about-accent-italic">Compliant</em>
          </h2>
          <p className="about-cert-sub">
            Industry-recognised certifications ensuring the highest standards
          </p>
          <div className="about-cert-tags">
            {certifications.map((c, i) => (
              <span
                key={i}
                className={"about-cert-tag" + (certVisible ? " visible" : "")}
                style={{ transitionDelay: i * 80 + "ms" }}
              >
                <span className="about-cert-dot" />
                {c}
              </span>
            ))}
          </div>
          <div className="about-cert-quote">
            <p>
              <span className="about-quote-highlight">
                "Your trusted partner in Geotechnical Engineering"
              </span>{" "}
              — We strive hard to ensure the work is executed safely and with high quality.
            </p>
            <div className="about-cert-values-row">
              <span>✦ SAFETY FIRST</span>
              <span>✦ QUALITY &amp; PRODUCTION</span>
              <span>✦ SEQUENTIAL WORK</span>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="about-team" ref={teamRef}>
        <div className="about-team-inner">
          <div className="about-section-label centered">
            <span className="about-label-line" />
            OUR TEAM
            <span className="about-label-line" />
          </div>
          <h2 className={"about-team-title" + (teamVisible ? " visible" : "")}>
            The People Behind<br />
            <em className="about-accent-italic">GSK Geotech</em>
          </h2>
          <p className="about-team-sub">
            Experienced professionals who bring decades of geotechnical expertise to every project across Singapore.
          </p>

          <div className="about-team-grid">
            {teamMembers.map((m, i) => (
              <div
                key={i}
                className={"about-team-card" + (teamVisible ? " visible" : "")}
                style={{ transitionDelay: i * 150 + "ms" }}
              >
                {/* Photo hidden for now — shows a circular initials badge instead
                    (matches the home page team carousel).
                    Bring the <img> back once real team photos are ready. */}
                <div className="about-team-avatar">
                  <span className="about-team-avatar-initials">{m.initials}</span>
                </div>
                <div className="about-team-info">
                  <h3 className="about-team-name">
                    <span className="about-accent">{m.name.charAt(0)}.</span>{" "}
                    {m.name.split(" ").slice(1).join(" ")}
                  </h3>
                  <div className="about-team-role">{m.tag}</div>
                  <div className="about-team-divider" />
                  <p className="about-team-desc">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      
    </div>
  );
}